import { MButtonType } from '../MButton/MButton.types';
import type { AriaAttributes } from 'react';
import { MContentSwapProps } from '../MContentSwap/MContentSwap.types';

export enum MContentHeaderAlignment {
  Left = 'Left',
  Centered = 'Centered',
}

export type MContentHeaderAllowedButtonType = MButtonType.Primary | MButtonType.Secondary | MButtonType.Tertiary;

export interface MContentHeaderEyebrow {
  eyebrowText?: string;
}

export interface MContentHeaderHeader {
  size?: string;
  text?: string;
  headerTag?: string;
}

export interface MContentHeaderBody {
  bodyText?: string;
  color?: string;
  size?: string;
}

export interface MContentHeaderTextBlock {
  alignment?: string;
  aEyebrow?: MContentHeaderEyebrow;
  aHeader?: MContentHeaderHeader;
  aBody?: MContentHeaderBody;
}

export interface MContentHeaderIcon {
  size?: string;
  icon?: string;
}

export interface MContentHeaderButton extends AriaAttributes {
  buttonText?: string;
  buttonType?: MContentHeaderAllowedButtonType;
  /** @deprecated AEM legacy name for buttonType */
  type?: string;
  size?: string;
  icon?: string;
  openInANewTab?: boolean | string;
  externalLink?: boolean;
  ctaLink?: string;
  aIcon?: MContentHeaderIcon;
}

/**
 * Minimal raw AEM button shape shared across organisms that need to resolve
 * `mButton ?? mContentSwap?.mButton` before the type-mapping boundary.
 * All fields are optional to accommodate every organism's authored model.
 *
 * This is an **input** type for `resolveRawButton` — it is NOT `MButtonProps`
 * (already-mapped) and NOT `MContentHeaderButton` (partially-mapped).
 */
export interface RawContentSwapButton {
  buttonText?: string;
  /** AEM legacy CTA field name (used by OCardCarousel / OCardCarouselCombo). */
  type?: string;
  /** Preferred CTA field name (used by OTabContainer / OCardCarouselCombo). */
  buttonType?: string;
  ctaLink?: string;
  openInANewTab?: string | boolean;
  externalLink?: string | boolean;
  size?: string;
  icon?: string;
  aIcon?: { size?: string; icon?: string };
}

export interface MContentHeaderItem {
  alignment?: MContentHeaderAlignment;
  cta?: 'true' | 'false';
  secondaryCta?: 'true' | 'false';
  mTextBlockSimple?: MContentHeaderTextBlock;
  mButton?: MContentHeaderButton;
  mButton2?: MContentHeaderButton;
  /**
   * Optional swap config for the CTA slot. When present, the header renders its
   * CTA slot from this config (switching on `mContentSwap.type` between buttons
   * and list-text-icons). When absent, the header falls back to building a
   * `Button`-type swap from the legacy `cta`/`mButton`/`secondaryCta`/`mButton2` props.
   */
  mContentSwap?: MContentSwapProps;
}

export interface MContentHeaderProps extends MContentHeaderItem {
  /** @deprecated Use flat props (`alignment`, `mTextBlockSimple`, `cta`, `mButton`, `secondaryCta`, `mButton2`) directly instead. This wrapper will be removed in a future release. */
  mContentHeader?: MContentHeaderItem;
}
